import type { DiskUsage } from './api/kb';
import type { TrashedDiskFile } from './wikiApi';

/**
 * Диск ↔ статьи и Корзина (владелец 22.10.2026): тексты и мелкая логика без React — чтобы тестировать.
 */

/** Сколько дней файл лежит в Корзине (совпадает с бэком: wiki-disk-trash.ts). */
export const TRASH_RETENTION_DAYS = 30;

/** Тост после сохранения статьи / удаления вложения, если вслед за ними файл ушёл с Диска. null — нечего показывать. */
export function diskTrashedMessage(files: ReadonlyArray<TrashedDiskFile> | null | undefined): string | null {
  const n = files?.length ?? 0;
  if (n === 0) return null;
  if (n === 1) return 'Файл удалён и с Диска (можно вернуть из Корзины 30 дней)';
  return `Файлы удалены и с Диска: ${n} (можно вернуть из Корзины 30 дней)`;
}

/** Сколько полных дней осталось до окончательного удаления из Корзины (не меньше 0). */
export function trashDaysLeft(expiresAt: string, now: Date = new Date()): number {
  const ms = new Date(expiresAt).getTime() - now.getTime();
  return Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / 86_400_000)) : 0;
}

const FILE_ID_RE = /\/wiki\/(?:disk\/files|attachments)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/** id файла (Диск или вложение) из адреса карточки; null — это не файл вики. */
export function fileIdFromUrl(url: string | null | undefined): string | null {
  const m = FILE_ID_RE.exec(url ?? '');
  return m ? m[1].toLowerCase() : null;
}

/** Что затронет удаление файла (или нескольких) на Диске — для окна подтверждения. */
export interface DeleteImpact {
  /** Статьи, где в тексте есть ссылка на файл: в них вместо карточки будет «Файл удалён». */
  linkedArticles: Array<{ slug: string; title: string }>;
  /** Статьи, из «Вложений» которых файл исчезнет. */
  attachmentArticles: Array<{ slug: string; title: string }>;
}

export function deleteImpact(usages: ReadonlyArray<DiskUsage | null>): DeleteImpact {
  const linked = new Map<string, { slug: string; title: string }>();
  const attached = new Map<string, { slug: string; title: string }>();
  for (const u of usages) {
    if (!u) continue;
    for (const a of u.articles) linked.set(a.slug, a);
    if (u.attachmentOf) attached.set(u.attachmentOf.slug, u.attachmentOf);
  }
  return { linkedArticles: [...linked.values()], attachmentArticles: [...attached.values()] };
}

/** Тост после удаления файла на Диске. */
export function diskDeletedMessage(r: { attachmentRemoved?: boolean; articleTitle?: string | null } | null | undefined): string {
  if (r?.attachmentRemoved) {
    return r.articleTitle
      ? `Файл в Корзине на 30 дней. Вложение убрано и из статьи «${r.articleTitle}»`
      : 'Файл в Корзине на 30 дней. Вложение убрано и из статьи';
  }
  return 'Файл в Корзине на 30 дней';
}
